// Fullscreen photo modal: hero zoom/pan, wheel, touch

interface PixelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type PhotoModalHeroOptions = {
  galleryUrls?: string[];
  galleryIndex?: number;
  onGalleryIndexChange?: (index: number) => void;
};

// ----- Fullscreen photo modal -----
const photoModalEl = document.getElementById('photo-modal');
const photoModalImgEl = document.getElementById('photo-modal-img') as HTMLImageElement | null;
const photoModalScrollerEl = document.getElementById('photo-modal-scroller');
const photoModalBackdropEl = document.getElementById('photo-modal-backdrop');
const photoModalCloseEl = document.getElementById('photo-modal-close');
const photoGalleryEl = document.getElementById('photo-modal-gallery');
const photoGalleryTrackEl = document.getElementById('photo-modal-gallery-track');
const photoGalleryDotsEl = document.getElementById('photo-modal-dots');

// Zoom/pan state for photo modal
var photoZoomScale = 1;
var photoZoomTx = 0;
var photoZoomTy = 0;
var photoZoomPanning = false;
var photoZoomPinching = false;
var photoZoomStartX = 0;
var photoZoomStartY = 0;
var photoZoomStartTx = 0;
var photoZoomStartTy = 0;
var photoZoomStartDist = 0;
var photoZoomStartScale = 1;
var photoZoomBaseW = 0;
var photoZoomBaseH = 0;
let photoGalleryUrls: string[] = [];
let photoGalleryIndex = 0;
let photoGalleryOnChange: ((index: number) => void) | null = null;
let photoGalleryMounted = false;
let photoGalleryGoTo: ((index: number, animate?: boolean) => void) | null = null;
let photoGalleryDragCancel: (() => void) | null = null;
let photoGalleryResizeObs: ResizeObserver | null = null;

function syncPhotoGalleryTrackPosition(animate = false): void {
  if (!photoGalleryMounted || photoGalleryUrls.length <= 1) return;
  const apply = (): void => {
    photoGalleryGoTo?.(photoGalleryIndex, animate);
  };
  requestAnimationFrame(() => requestAnimationFrame(apply));
}

function ensurePhotoGalleryResizeObserver(): void {
  if (!photoGalleryEl || photoGalleryResizeObs) return;
  if (typeof ResizeObserver === 'undefined') return;
  photoGalleryResizeObs = new ResizeObserver(() => {
    if (photoGalleryMounted) photoGalleryGoTo?.(photoGalleryIndex, false);
  });
  photoGalleryResizeObs.observe(photoGalleryEl);
}
let photoHeroFromRect: PixelRect | null = null;
let photoHeroSourceEl: HTMLImageElement | null = null;
let photoHeroSourceRadius = 0;
let photoHeroCloneEl: HTMLImageElement | null = null;
var photoHeroAnimToken = 0;
var photoHeroTransition =
  'transform 0.34s cubic-bezier(0.18, 0.95, 0.2, 1), border-radius 0.34s cubic-bezier(0.18, 0.95, 0.2, 1)';

function photoHeroAbortActive() {
  // Invalidate any pending finish() from previous animations.
  photoHeroAnimToken += 1;
  try {
    if (photoHeroCloneEl) photoHeroCloneEl.remove();
  } catch (_) {}
  photoHeroCloneEl = null;
  try {
    document.querySelectorAll('.photo-hero-clone').forEach((node) => {
      node.remove();
    });
  } catch (_) {}
  // If we abort mid-opening, ensure we don't keep "opening" state around.
  try { document.body.classList.remove('photo-hero-opening'); } catch (_) {}
  try { document.body.classList.remove('photo-hero-closing'); } catch (_) {}
}

function photoHeroReadBorderRadiusPx(fromImgEl: HTMLImageElement): number {
  if (!fromImgEl) return 0;
  try {
    var bubbleWrap = fromImgEl.closest ? fromImgEl.closest('.chat-bubble-image') : null;
    var el = bubbleWrap || fromImgEl;
    var br = window.getComputedStyle(el).borderRadius || '0px';
    var parts = String(br).split(/\s+/);
    var parsed = 0;
    for (var i = 0; i < parts.length; i++) {
      var m = /^([\d.]+)px$/.exec(parts[i]);
      if (m) parsed = Math.max(parsed, parseFloat(m[1]));
    }
    if (parsed <= 0 && bubbleWrap) parsed = 14;
    if (parsed > 0 && fromImgEl.getBoundingClientRect) {
      var rect = fromImgEl.getBoundingClientRect();
      var maxR = Math.min(rect.width, rect.height) / 2;
      if (maxR > 0) parsed = Math.min(parsed, maxR);
    }
    return parsed > 0 ? parsed : 0;
  } catch (_) {
    return 0;
  }
}

function photoHeroRadiusForTransform(visualRadiusPx: number, scale: number): number {
  if (!visualRadiusPx || visualRadiusPx <= 0) return 0;
  return visualRadiusPx / Math.max(Math.abs(scale), 0.0001);
}
let photoPanelsRestoreTimer: ReturnType<typeof setTimeout> | null = null;
let photoOpenDelayTimer: ReturnType<typeof setTimeout> | null = null;
let photoModalSession = 0;
let photoImgLoadId = 0;
let photoOpenBlockedUntil = 0;
let lastCartNavGestureAt = 0;
let lastCartNavGestureY = 0;
let cartNavLockTimer: ReturnType<typeof setTimeout> | null = null;
var PHOTO_OPEN_DELAY_MS = 0;
var photoMousePanning = false;
var photoWheelTargetScale = 1;
let photoWheelAnimRaf: number | null = null;
var photoWheelAnimPrevTs = 0;
var PHOTO_WHEEL_SMOOTH_TAU_MS = 95;
var isDesktopLikeDevice = (function () {
  try {
    if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) return true;
  } catch (_) {}
  try {
    var ua = String((navigator && navigator.userAgent) || '').toLowerCase();
    if (ua.indexOf('windows') >= 0 || ua.indexOf('macintosh') >= 0 || ua.indexOf('x11') >= 0) return true;
  } catch (_) {}
  return false;
})();

function setPhotoUiLocked(locked: boolean): void {
  try {
    var appEl = document.querySelector('.app');
    if (appEl) appEl.classList.toggle('photo-ui-locked', !!locked);
  } catch (_) {}
  try {
    var tabbarEl = document.querySelector('.app-tabbar');
    if (tabbarEl) tabbarEl.classList.toggle('photo-ui-locked', !!locked);
  } catch (_) {}
  try {
    document.body.classList.toggle('photo-modal-scroll-lock', !!locked);
  } catch (_) {}
}

function invalidatePhotoImgLoads(): void {
  photoImgLoadId += 1;
  if (!photoModalImgEl) return;
  try {
    photoModalImgEl.onload = null;
    photoModalImgEl.onerror = null;
  } catch (_) {}
}

function isPhotoOpenBlocked(): boolean {
  if (Date.now() < photoOpenBlockedUntil) return true;
  const cartSheet = document.getElementById('cart-sheet');
  if (
    cartSheet &&
    (cartSheet.classList.contains('sheet-open') || cartSheet.classList.contains('sheet-closing'))
  ) {
    return true;
  }
  return false;
}

function setPresetCartNavLock(active: boolean): void {
  try {
    document.body.classList.toggle('preset-cart-nav-lock', active);
  } catch {
    /* ignore */
  }
}

export function recordCartNavigateGesture(clientY: number): void {
  lastCartNavGestureAt = Date.now();
  lastCartNavGestureY = clientY;
  photoOpenBlockedUntil = Date.now() + 2500;
  setPresetCartNavLock(true);
  if (cartNavLockTimer) {
    try {
      clearTimeout(cartNavLockTimer);
    } catch {
      /* ignore */
    }
  }
  cartNavLockTimer = setTimeout(function () {
    cartNavLockTimer = null;
    setPresetCartNavLock(false);
  }, 700);
}

/** Bottom bar shows «Перейти в корзину» — zoom по фото в этом режиме не нужен. */
export function isPresetDetailGoCartActive(): boolean {
  const bar = document.getElementById('preset-detail-bottom-bar');
  const btn = document.getElementById('preset-detail-go-cart-btn') as HTMLButtonElement | null;
  return !!(
    bar &&
    bar.classList.contains('preset-detail-bottom-bar--in-cart') &&
    btn &&
    !btn.disabled &&
    btn.getAttribute('aria-hidden') !== 'true'
  );
}

function shouldBlockPresetDetailPhotoOpen(): boolean {
  if (isPhotoOpenBlocked()) return true;
  if (isPresetDetailGoCartActive()) return true;
  if (Date.now() - lastCartNavGestureAt < 2500) return true;
  return false;
}

export function isPresetDetailPhotoOpenSuppressed(): boolean {
  return shouldBlockPresetDetailPhotoOpen();
}

export function isPointerInPresetDetailBottomBar(ev: Event): boolean {
  const bar = document.getElementById('preset-detail-bottom-bar');
  if (!bar) return false;
  let x = 0;
  let y = 0;
  if ('clientX' in ev && 'clientY' in ev) {
    x = Number((ev as MouseEvent).clientX) || 0;
    y = Number((ev as MouseEvent).clientY) || 0;
  } else if ('changedTouches' in ev) {
    const t = (ev as TouchEvent).changedTouches?.[0];
    if (t) {
      x = t.clientX;
      y = t.clientY;
    }
  }
  // Android/Telegram WebView: synthesized click часто приходит с 0,0 — не считаем «вне панели».
  if (!x && !y) return isPresetDetailGoCartActive();
  try {
    const br = bar.getBoundingClientRect();
    return x >= br.left && x <= br.right && y >= br.top && y <= br.bottom;
  } catch {
    return false;
  }
}

function clearPhotoOpenDelayTimer() {
  try {
    if (photoOpenDelayTimer) {
      clearTimeout(photoOpenDelayTimer);
      photoOpenDelayTimer = null;
    }
  } catch (_) {}
}

function runPhotoOpenWithDelay(startOpen: () => void): void {
  clearPhotoOpenDelayTimer();
  const session = photoModalSession;
  const run = (): void => {
    if (session !== photoModalSession) return;
    if (isPhotoOpenBlocked()) return;
    if (shouldBlockPresetDetailPhotoOpen()) return;
    try {
      startOpen();
    } catch (_) {}
  };
  if (PHOTO_OPEN_DELAY_MS <= 0) {
    requestAnimationFrame(run);
    return;
  }
  photoOpenDelayTimer = setTimeout(function () {
    photoOpenDelayTimer = null;
    run();
  }, PHOTO_OPEN_DELAY_MS);
}

function setPhotoSourceBgHidden(hidden: boolean): void {
  try { document.body.classList.toggle('photo-source-bg-hidden', !!hidden); } catch (_) {}
}

function hidePresetDetailPanelsForPhoto() {
  try {
    if (photoPanelsRestoreTimer) {
      clearTimeout(photoPanelsRestoreTimer);
      photoPanelsRestoreTimer = null;
    }
  } catch (_) {}
  try { document.body.classList.remove('photo-panels-restoring'); } catch (_) {}
  try { document.body.classList.remove('photo-panels-restored'); } catch (_) {}
  try { document.body.classList.add('photo-panels-hidden'); } catch (_) {}
}

function restorePresetDetailPanelsAfterPhoto() {
  try {
    if (photoPanelsRestoreTimer) {
      clearTimeout(photoPanelsRestoreTimer);
      photoPanelsRestoreTimer = null;
    }
  } catch (_) {}
  try { document.body.classList.remove('photo-panels-hidden'); } catch (_) {}
  try { document.body.classList.remove('photo-panels-restored'); } catch (_) {}
  try { document.body.classList.add('photo-panels-restoring'); } catch (_) {}
  photoPanelsRestoreTimer = setTimeout(function () {
    try { document.body.classList.remove('photo-panels-restoring'); } catch (_) {}
    try { document.body.classList.add('photo-panels-restored'); } catch (_) {}
    photoPanelsRestoreTimer = null;
  }, 360);
}

function updatePhotoModalHeaderOffset() {
  if (!photoModalEl) return;
  var offset = 0;
  try {
    var detailView = document.getElementById('view-preset-detail');
    var header = detailView ? detailView.querySelector('.page-header') : null;
    if (header) {
      var rect = header.getBoundingClientRect();
      offset = Math.max(0, Math.ceil(rect.height || 0));
    }
  } catch (_) {}
  try { photoModalEl.style.setProperty('--photo-modal-header-offset', offset + 'px'); } catch (_) {}
  return offset;
}

function clamp(n: number, a: number, b: number): number { return Math.max(a, Math.min(b, n)); }

function clearPhotoGallery(): void {
  unmountPhotoGalleryTrack();
  photoGalleryUrls = [];
  photoGalleryIndex = 0;
  photoGalleryOnChange = null;
}

function getPhotoZoomImg(): HTMLImageElement | null {
  if (photoGalleryMounted && photoGalleryUrls.length > 1 && photoGalleryTrackEl) {
    const slide = photoGalleryTrackEl.children[photoGalleryIndex];
    const img = slide?.querySelector('img');
    if (img instanceof HTMLImageElement) return img;
  }
  return photoModalImgEl;
}

function updatePhotoGalleryDots(idx: number): void {
  if (!photoGalleryDotsEl) return;
  photoGalleryDotsEl.querySelectorAll('.photo-modal-dot').forEach((dot, i) => {
    dot.classList.toggle('photo-modal-dot-active', i === idx);
  });
}

function mountPhotoGalleryTrack(startIndex: number): void {
  if (!photoGalleryEl || !photoGalleryTrackEl || photoGalleryUrls.length <= 1) return;
  photoGalleryTrackEl.textContent = '';
  for (const url of photoGalleryUrls) {
    const slide = document.createElement('div');
    slide.className = 'photo-modal-gallery-slide';
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    slide.appendChild(img);
    photoGalleryTrackEl.appendChild(slide);
  }
  if (photoGalleryDotsEl) {
    photoGalleryDotsEl.textContent = '';
    for (let d = 0; d < photoGalleryUrls.length; d++) {
      const dot = document.createElement('span');
      dot.className =
        'photo-modal-dot' + (d === startIndex ? ' photo-modal-dot-active' : '');
      photoGalleryDotsEl.appendChild(dot);
    }
    photoGalleryDotsEl.hidden = false;
  }
  photoGalleryEl.hidden = false;
  photoGalleryMounted = true;
  try {
    photoModalEl?.classList.add('photo-modal-gallery-active');
  } catch (_) {}
  photoGalleryIndex = clamp(startIndex, 0, photoGalleryUrls.length - 1);
  ensurePhotoGalleryResizeObserver();
  syncPhotoGalleryTrackPosition(false);
  photoZoomSyncGalleryZoomState();
}

function unmountPhotoGalleryTrack(): void {
  photoGalleryMounted = false;
  try {
    photoModalEl?.classList.remove('photo-modal-gallery-active', 'photo-modal-gallery-zoomed');
    photoGalleryEl?.classList.remove('photo-modal-gallery-zoomed');
  } catch (_) {}
  if (photoGalleryTrackEl) photoGalleryTrackEl.textContent = '';
  if (photoGalleryEl) photoGalleryEl.hidden = true;
  if (photoGalleryDotsEl) {
    photoGalleryDotsEl.textContent = '';
    photoGalleryDotsEl.hidden = true;
  }
  if (photoModalImgEl) {
    try {
      photoModalImgEl.style.display = '';
      photoModalImgEl.style.transform = '';
    } catch (_) {}
  }
  if (photoGalleryTrackEl) {
    photoGalleryTrackEl.querySelectorAll('img').forEach((img) => {
      if (img instanceof HTMLImageElement) img.style.transform = '';
    });
  }
}

function initPhotoModalGalleryCarousel(): void {
  if (!photoGalleryEl || !photoGalleryTrackEl) return;
  const root = photoGalleryEl;
  const track = photoGalleryTrackEl;
  const SNAP_MS = 320;
  const SNAP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  let idx = 0;
  let startX = 0;
  let curX = 0;
  let dragging = false;
  let dragDistance = 0;
  let lastMoveX = 0;
  let lastMoveT = 0;
  let lastVelocityX = 0;

  function width(): number {
    return root.clientWidth || 1;
  }

  function applyPx(px: number, animate: boolean): void {
    track.style.transition = animate ? 'transform ' + SNAP_MS + 'ms ' + SNAP_EASE : 'none';
    track.style.transform = 'translate3d(' + px + 'px, 0, 0)';
  }

  function setIndex(next: number, animate = true): void {
    if (!photoGalleryMounted || photoGalleryUrls.length <= 1) return;
    idx = clamp(next, 0, photoGalleryUrls.length - 1);
    photoGalleryIndex = idx;
    applyPx(-idx * width(), animate);
    updatePhotoGalleryDots(idx);
    photoZoomSyncGalleryZoomState();
    if (photoZoomScale > 1.01) photoZoomReset();
  }

  photoGalleryGoTo = setIndex;

  function canDrag(): boolean {
    if (!photoGalleryMounted || photoGalleryUrls.length <= 1) return false;
    if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) return false;
    if (photoModalEl.classList.contains('hero-animating')) return false;
    if (photoZoomScale > 1.01) return false;
    if (photoZoomPinching) return false;
    return true;
  }

  function onDown(clientX: number, pointerId: number | null): void {
    if (!canDrag()) return;
    dragging = true;
    dragDistance = 0;
    startX = clientX;
    curX = clientX;
    lastMoveX = clientX;
    lastMoveT = performance.now();
    lastVelocityX = 0;
    root.classList.add('photo-modal-gallery-dragging');
    if (pointerId != null) {
      try {
        root.setPointerCapture(pointerId);
      } catch (_) {}
    }
    applyPx(-idx * width(), false);
    photoWheelAnimStop();
  }

  function onMove(clientX: number): void {
    if (!dragging) return;
    const now = performance.now();
    const dt = now - lastMoveT;
    if (dt > 0) lastVelocityX = (clientX - lastMoveX) / dt;
    lastMoveX = clientX;
    lastMoveT = now;
    curX = clientX;
    const dx = curX - startX;
    dragDistance = Math.max(dragDistance, Math.abs(dx));
    const w = width();
    let px = -idx * w + dx;
    const last = photoGalleryUrls.length - 1;
    if (idx === 0 && dx > 0) px = dx * 0.35;
    else if (idx === last && dx < 0) px = -idx * w + dx * 0.35;
    applyPx(px, false);
  }

  function onUp(pointerId: number | null): void {
    if (!dragging) return;
    dragging = false;
    root.classList.remove('photo-modal-gallery-dragging');
    if (pointerId != null) {
      try {
        root.releasePointerCapture(pointerId);
      } catch (_) {}
    }
    const dx = curX - startX;
    const w = width();
    const distanceThreshold = Math.min(40, Math.max(28, w * 0.12));
    const flingThreshold = 0.28;
    const recentFling = performance.now() - lastMoveT < 80;
    const velocity = recentFling ? lastVelocityX : 0;
    if (dx > distanceThreshold || velocity > flingThreshold) setIndex(idx - 1);
    else if (dx < -distanceThreshold || velocity < -flingThreshold) setIndex(idx + 1);
    else setIndex(idx);
    if (dragDistance > 8) {
      const blockClick = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
      };
      root.addEventListener('click', blockClick, { capture: true, once: true });
    }
  }

  function cancelDrag(): void {
    if (!dragging) return;
    dragging = false;
    root.classList.remove('photo-modal-gallery-dragging');
    applyPx(-idx * width(), false);
  }

  photoGalleryDragCancel = cancelDrag;

  root.addEventListener('pointerdown', (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Touch swipe handled separately — avoids racing with pinch on scroller
    if (e.pointerType === 'touch') return;
    onDown(e.clientX, e.pointerId);
  });
  root.addEventListener('pointermove', (e: PointerEvent) => {
    if (!dragging) return;
    if (e.pointerType === 'mouse' && (e.buttons & 1) === 0) {
      onUp(e.pointerId);
      return;
    }
    onMove(e.clientX);
    if (dragDistance > 2) e.preventDefault();
  });
  root.addEventListener('pointerup', (e: PointerEvent) => {
    onUp(e.pointerId);
  });
  root.addEventListener('pointercancel', (e: PointerEvent) => {
    onUp(e.pointerId);
  });
  root.addEventListener('lostpointercapture', () => {
    if (dragging) onUp(null);
  });
  root.addEventListener('dragstart', (e) => {
    e.preventDefault();
  });

  root.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      if (!canDrag()) return;
      if (!e.touches || e.touches.length !== 1) return;
      onDown(e.touches[0].clientX, null);
    },
    { passive: false }
  );
  root.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      if (!dragging) return;
      if (photoZoomPinching) {
        cancelDrag();
        return;
      }
      if (!e.touches || e.touches.length !== 1) return;
      onMove(e.touches[0].clientX);
      if (dragDistance > 2) e.preventDefault();
    },
    { passive: false }
  );
  root.addEventListener('touchend', () => {
    if (dragging) onUp(null);
  });
  root.addEventListener('touchcancel', () => {
    cancelDrag();
  });

  window.addEventListener('resize', () => {
    if (photoGalleryMounted) applyPx(-idx * width(), false);
  });
}

function photoHeroPixelRectFromImg(fromImgEl: HTMLImageElement): PixelRect | null {
  let fromRect: DOMRect | null = null;
  try {
    fromRect = fromImgEl.getBoundingClientRect();
  } catch (_) {
    fromRect = null;
  }
  if (!fromRect || !fromRect.width || !fromRect.height) return null;
  try {
    if (fromImgEl.naturalWidth && fromImgEl.naturalHeight) {
      const bw = fromRect.width;
      const bh = fromRect.height;
      const nw = fromImgEl.naturalWidth;
      const nh = fromImgEl.naturalHeight;
      const sFrom = Math.min(bw / nw, bh / nh);
      const pw = nw * sFrom;
      const ph = nh * sFrom;
      return {
        left: fromRect.left + (bw - pw) / 2,
        top: fromRect.top + (bh - ph) / 2,
        width: pw,
        height: ph
      };
    }
  } catch (_) {}
  return {
    left: fromRect.left,
    top: fromRect.top,
    width: fromRect.width,
    height: fromRect.height
  };
}

function preparePhotoHeroCloseTarget(): void {
  if (photoGalleryUrls.length <= 1) return;
  try {
    photoGalleryOnChange?.(photoGalleryIndex);
  } catch (_) {}
  const carousel =
    photoHeroSourceEl?.closest('.preset-detail-carousel') ||
    document.querySelector(
      '#view-preset-detail .preset-detail-carousel, #view-portfolio-detail .preset-detail-carousel'
    );
  if (!carousel) return;
  void (carousel as HTMLElement).offsetHeight;
  carousel.querySelectorAll('.preset-detail-carousel-slide img').forEach((node) => {
    if (node instanceof HTMLImageElement) node.style.visibility = '';
  });
  const imgs = carousel.querySelectorAll('.preset-detail-carousel-slide img');
  const targetImg = imgs[photoGalleryIndex];
  if (!(targetImg instanceof HTMLImageElement)) return;
  photoHeroSourceEl = targetImg;
  const rect = photoHeroPixelRectFromImg(targetImg);
  if (rect) photoHeroFromRect = rect;
  photoHeroSourceRadius = photoHeroReadBorderRadiusPx(targetImg);
  try {
    targetImg.style.visibility = 'hidden';
  } catch (_) {}
}

function photoZoomSyncGalleryZoomState(): void {
  const zoomed = photoGalleryMounted && photoGalleryUrls.length > 1 && photoZoomScale > 1.01;
  try {
    photoModalEl?.classList.toggle('photo-modal-gallery-zoomed', zoomed);
    photoGalleryEl?.classList.toggle('photo-modal-gallery-zoomed', zoomed);
  } catch (_) {}
  if (!photoGalleryTrackEl) return;
  photoGalleryTrackEl.querySelectorAll('.photo-modal-gallery-slide').forEach((slide, i) => {
    slide.classList.toggle('photo-modal-gallery-slide-active', i === photoGalleryIndex);
  });
}

function photoZoomUpdatePanCursor() {
  if (!photoModalScrollerEl || !isDesktopLikeDevice) return;
  try {
    photoModalScrollerEl.classList.toggle('photo-pan-enabled', photoZoomScale > 1.01);
    photoModalScrollerEl.classList.toggle('photo-panning', !!photoMousePanning || !!photoZoomPanning);
  } catch (_) {}
}

function photoWheelAnimStop() {
  if (!photoWheelAnimRaf) return;
  try { cancelAnimationFrame(photoWheelAnimRaf); } catch (_) {}
  photoWheelAnimRaf = null;
  photoWheelAnimPrevTs = 0;
}

function photoWheelAnimStart() {
  if (photoWheelAnimRaf) return;
  photoWheelAnimPrevTs = 0;
  function tick(ts: number): void {
    if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) {
      photoWheelAnimRaf = null;
      photoWheelAnimPrevTs = 0;
      return;
    }
    if (!photoWheelAnimPrevTs) photoWheelAnimPrevTs = ts;
    var dt = clamp(ts - photoWheelAnimPrevTs, 1, 40);
    photoWheelAnimPrevTs = ts;
    // Экспоненциальное сглаживание: устойчиво даже при очень частых wheel-событиях.
    var alpha = 1 - Math.exp(-dt / PHOTO_WHEEL_SMOOTH_TAU_MS);
    photoZoomScale = photoZoomScale + (photoWheelTargetScale - photoZoomScale) * alpha;
    // Плавно возвращаем смещение в центр при откате к 100%, без резкого сброса tx/ty
    // (раньше мгновенный snap давал дергание в конце анимации).
    if (photoWheelTargetScale <= 1.001) {
      var panTau = PHOTO_WHEEL_SMOOTH_TAU_MS * 0.72;
      var panAlpha = 1 - Math.exp(-dt / panTau);
      photoZoomTx += -photoZoomTx * panAlpha;
      photoZoomTy += -photoZoomTy * panAlpha;
    }
    photoZoomApply();
    var eps = photoWheelTargetScale <= 1.002 ? 0.0035 : 0.0012;
    if (Math.abs(photoWheelTargetScale - photoZoomScale) <= eps) {
      photoZoomScale = photoWheelTargetScale;
      // Уже фактически 100% и цель базовая — доводим до идеальных 1,0,0 одним маленьким шагом
      if (photoWheelTargetScale <= 1 + 8e-4) {
        photoZoomScale = 1;
        photoZoomTx = 0;
        photoZoomTy = 0;
      }
      photoZoomApply();
      photoWheelAnimRaf = null;
      photoWheelAnimPrevTs = 0;
      return;
    }
    photoWheelAnimRaf = requestAnimationFrame(tick);
  }
  photoWheelAnimRaf = requestAnimationFrame(tick);
}

function startPhotoModalBackdropFadeIn() {
  if (!photoModalEl) return;
  try { photoModalEl.classList.remove('photo-modal-fade-in'); } catch (_) {}
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      try {
        if (photoModalEl && photoModalEl.classList.contains('photo-modal-open')) {
          photoModalEl.classList.add('photo-modal-fade-in');
        }
      } catch (_) {}
    });
  });
}

function photoZoomMeasureBase(force = false) {
  const imgEl = getPhotoZoomImg();
  if (!imgEl || !photoModalScrollerEl) return;
  if (!force && photoZoomScale > 1.01 && photoZoomBaseW > 0 && photoZoomBaseH > 0) return;
  try {
    var r = imgEl.getBoundingClientRect();
    var cr = photoModalScrollerEl.getBoundingClientRect();
    var s = Math.max(photoZoomScale, 1);
    // getBoundingClientRect includes current scale — normalize to 1x contain size
    photoZoomBaseW = Math.min((r.width || 0) / s, cr.width || 0);
    photoZoomBaseH = Math.min((r.height || 0) / s, cr.height || 0);
  } catch (_) {}
}

function photoZoomApply() {
  const imgEl = getPhotoZoomImg();
  if (!imgEl || !photoModalScrollerEl) return;
  var scale = clamp(photoZoomScale, 1, 4);
  photoZoomScale = scale;
  photoZoomMeasureBase();
  try {
    var cr = photoModalScrollerEl.getBoundingClientRect();
    var maxTx = Math.max(0, (photoZoomBaseW * scale - cr.width) / 2);
    var maxTy = Math.max(0, (photoZoomBaseH * scale - cr.height) / 2);
    photoZoomTx = clamp(photoZoomTx, -maxTx, maxTx);
    photoZoomTy = clamp(photoZoomTy, -maxTy, maxTy);
  } catch (_) {}
  try {
    imgEl.style.transform =
      'translate3d(' + photoZoomTx.toFixed(2) + 'px,' + photoZoomTy.toFixed(2) + 'px,0) scale(' + photoZoomScale.toFixed(4) + ')';
  } catch (_) {}
  try { photoZoomUpdatePanCursor(); } catch (_) {}
  try { photoZoomSyncGalleryZoomState(); } catch (_) {}
}

function photoZoomReset() {
  photoWheelAnimStop();
  photoZoomScale = 1;
  photoWheelTargetScale = 1;
  photoZoomTx = 0;
  photoZoomTy = 0;
  photoZoomPanning = false;
  photoMousePanning = false;
  photoZoomPinching = false;
  photoZoomStartDist = 0;
  photoZoomStartScale = 1;
  photoZoomBaseW = 0;
  photoZoomBaseH = 0;
  try {
    if (photoModalImgEl) photoModalImgEl.style.transform = '';
  } catch (_) {}
  try {
    photoGalleryTrackEl?.querySelectorAll('img').forEach((node) => {
      if (node instanceof HTMLImageElement) node.style.transform = '';
    });
  } catch (_) {}
  photoZoomApply();
}

export function openPhotoModal(src: string): void {
  if (isPhotoOpenBlocked()) return;
  if (shouldBlockPresetDetailPhotoOpen()) return;
  const modalEl = photoModalEl;
  const imgEl = photoModalImgEl;
  if (!modalEl || !imgEl || !photoModalScrollerEl) return;
  const url = (src || '').trim();
  if (!url) return;
  try { updatePhotoModalHeaderOffset(); } catch (_) {}
  try { hidePresetDetailPanelsForPhoto(); } catch (_) {}
  try { setPhotoUiLocked(true); } catch (_) {}
  const session = photoModalSession;
  runPhotoOpenWithDelay(function () {
    if (session !== photoModalSession) return;
    try { modalEl.classList.remove('photo-modal-closing'); } catch (_) {}
    imgEl.src = url;
    try { setPhotoSourceBgHidden(true); } catch (_) {}
    modalEl.classList.add('photo-modal-open');
    try { startPhotoModalBackdropFadeIn(); } catch (_) {}
    modalEl.setAttribute('aria-hidden', 'false');

    photoZoomReset();

    // After load, re-measure base contain size.
    const loadSession = photoModalSession;
    const loadId = photoImgLoadId;
    imgEl.onload = function () {
      if (loadSession !== photoModalSession || loadId !== photoImgLoadId) return;
      try { photoZoomMeasureBase(); } catch (_) {}
      try { photoZoomApply(); } catch (_) {}
    };
  });
}

export function openPhotoModalHero(
  fromImgEl: HTMLImageElement,
  sourceEvent?: Event,
  options?: PhotoModalHeroOptions
): void {
  if (!fromImgEl || !photoModalEl || !photoModalImgEl) return;
  if (fromImgEl.closest('#view-preset-detail')) {
    if (sourceEvent && isPointerInPresetDetailBottomBar(sourceEvent)) return;
    if (shouldBlockPresetDetailPhotoOpen()) return;
  } else if (isPhotoOpenBlocked()) {
    return;
  }
  const modalEl = photoModalEl;
  const imgEl = photoModalImgEl;
  clearPhotoGallery();
  if (options?.galleryUrls && options.galleryUrls.length > 1) {
    photoGalleryUrls = options.galleryUrls.slice();
    photoGalleryIndex = clamp(options.galleryIndex ?? 0, 0, photoGalleryUrls.length - 1);
    photoGalleryOnChange = options.onGalleryIndexChange ?? null;
  }
  var src = (fromImgEl.getAttribute && fromImgEl.getAttribute('src')) ? String(fromImgEl.getAttribute('src') || '').trim() : '';
  if (photoGalleryUrls.length > 1) {
    src = photoGalleryUrls[photoGalleryIndex] || src;
  }
  if (!src) return;

  // If a hero animation is already running (opening/closing), kill its clone & callbacks.
  photoHeroAbortActive();
  var token = photoHeroAnimToken;

  var fromRect = null;
  try { fromRect = fromImgEl.getBoundingClientRect(); } catch (_) { fromRect = null; }
  if (!fromRect || !fromRect.width || !fromRect.height) {
    openPhotoModal(src);
    return;
  }

  // Convert thumbnail element rect to "pixel bounds" inside it,
  // because the thumbnail image also uses `object-fit: contain`.
  const fromPixelRect = photoHeroPixelRectFromImg(fromImgEl);
  if (!fromPixelRect) {
    openPhotoModal(src);
    return;
  }

  // Open modal; hide real image until hero finishes
  photoHeroFromRect = fromPixelRect;
  photoHeroSourceRadius = photoHeroReadBorderRadiusPx(fromImgEl);
  try { updatePhotoModalHeaderOffset(); } catch (_) {}
  try { hidePresetDetailPanelsForPhoto(); } catch (_) {}
  try { setPhotoUiLocked(true); } catch (_) {}
  const session = photoModalSession;
  runPhotoOpenWithDelay(function () {
    if (session !== photoModalSession) return;
    // If user closed before delay fired, abort.
    if (token !== photoHeroAnimToken) return;
    if (photoGalleryUrls.length > 1) {
      mountPhotoGalleryTrack(photoGalleryIndex);
    }
    try { setPhotoSourceBgHidden(true); } catch (_) {}
    // Hide the source image while the hero clone is visible (avoid double image).
    try {
      photoHeroSourceEl = fromImgEl;
    } catch (_) {
      photoHeroSourceEl = null;
    }
    imgEl.style.opacity = '0';
    try { modalEl.classList.remove('photo-modal-closing'); } catch (_) {}
    modalEl.classList.add('photo-modal-open');
    try { startPhotoModalBackdropFadeIn(); } catch (_) {}
    modalEl.classList.add('hero-animating');
    try { document.body.classList.add('photo-hero-opening'); } catch (_) {}
    modalEl.setAttribute('aria-hidden', 'false');
    imgEl.src = src;
    photoZoomReset();

    var clone = document.createElement('img');
    clone.className = 'photo-hero-clone';
    clone.src = src;
    clone.alt = '';
    clone.style.left = fromPixelRect.left + 'px';
    clone.style.top = fromPixelRect.top + 'px';
    clone.style.width = fromPixelRect.width + 'px';
    clone.style.height = fromPixelRect.height + 'px';
    clone.style.borderRadius = (photoHeroSourceRadius > 0 ? photoHeroSourceRadius : 0) + 'px';
    try { if (photoHeroCloneEl) photoHeroCloneEl.remove(); } catch (_) {}
    photoHeroCloneEl = clone;
    document.body.appendChild(clone);

    function finalizeHeroOpen(): void {
      if (token !== photoHeroAnimToken || session !== photoModalSession) return;
      try { if (photoHeroCloneEl === clone) photoHeroCloneEl = null; } catch (_) {}
      try { clone.remove(); } catch (_) {}
      if (photoGalleryUrls.length <= 1) {
        try { imgEl.style.opacity = '1'; } catch (_) {}
      } else {
        syncPhotoGalleryTrackPosition(false);
      }
      try { modalEl.classList.remove('hero-animating'); } catch (_) {}
      try { document.body.classList.remove('photo-hero-opening'); } catch (_) {}
      try { photoZoomMeasureBase(); } catch (_) {}
      try { photoZoomApply(); } catch (_) {}
    }

    function finish(): void {
      if (photoGalleryUrls.length > 1) {
        const slideImg = getPhotoZoomImg();
        if (slideImg && slideImg.complete && slideImg.naturalWidth > 0) {
          finalizeHeroOpen();
          return;
        }
        let finalized = false;
        const run = (): void => {
          if (finalized) return;
          finalized = true;
          finalizeHeroOpen();
        };
        if (slideImg) slideImg.addEventListener('load', run, { once: true });
        setTimeout(run, 160);
        return;
      }
      finalizeHeroOpen();
    }

    const heroLoadId = photoImgLoadId;
    imgEl.onload = function () {
      if (session !== photoModalSession || heroLoadId !== photoImgLoadId) return;
      function runHero(): void {
      if (
        token !== photoHeroAnimToken ||
        session !== photoModalSession ||
        heroLoadId !== photoImgLoadId
      ) {
        try { clone.remove(); } catch (_) {}
        return;
      }
      // Compute target rect using contain math inside the scroller box.
      let toRect: PixelRect | null = null;
      const heroFromRect = photoHeroFromRect;
      if (!heroFromRect) {
        finish();
        return;
      }
      try {
        if (photoModalScrollerEl && imgEl.naturalWidth && imgEl.naturalHeight) {
          const cr = photoModalScrollerEl.getBoundingClientRect();
          const innerW = Math.max(1, cr.width);
          const innerH = Math.max(1, cr.height);
          const nw = imgEl.naturalWidth;
          const nh = imgEl.naturalHeight;
          var sc = Math.min(innerW / nw, innerH / nh);
          var tw = nw * sc;
          var th = nh * sc;
          var left = cr.left + (innerW - tw) / 2;
          var top = cr.top + (innerH - th) / 2;
          toRect = { left: left, top: top, width: tw, height: th };
        }
      } catch (_) { toRect = null; }
      if (!toRect || !toRect.width || !toRect.height) {
        finish();
        return;
      }
      const dx = toRect.left - heroFromRect.left;
      const dy = toRect.top - heroFromRect.top;
      const s = toRect.width / heroFromRect.width;
      var startVisualR = photoHeroSourceRadius > 0 ? photoHeroSourceRadius : 0;

        clone.style.transformOrigin = 'top left';
        clone.style.transition = photoHeroTransition;
        clone.style.borderRadius = startVisualR > 0 ? startVisualR + 'px' : '0px';
        requestAnimationFrame(function () {
          clone.style.transform =
            'translate3d(' + dx.toFixed(2) + 'px,' + dy.toFixed(2) + 'px,0) scale(' + s.toFixed(4) + ')';
          clone.style.borderRadius = '0px';
        });
        clone.addEventListener('transitionend', function te(ev: TransitionEvent) {
          if (ev && ev.propertyName && String(ev.propertyName) !== 'transform' && String(ev.propertyName) !== 'border-radius') return;
          finish();
        }, { once: true });
        setTimeout(function () {
          if (token !== photoHeroAnimToken || session !== photoModalSession) return;
          finish();
        }, 420);
      }
  
      // Decode before starting animation to avoid jank/teleport.
      try {
        if (imgEl.decode) {
          imgEl.decode().then(runHero).catch(runHero);
          return;
        }
      } catch (_) {}
      runHero();
    };
  });
}

function closePhotoModalHero(): void {
  if (!photoHeroFromRect || !photoModalEl || !photoModalImgEl || !photoModalScrollerEl) {
    closePhotoModal();
    return;
  }
  const modalEl = photoModalEl;
  const imgEl = photoModalImgEl;

  // If we're closing while opening hero is still running, kill its clone & callbacks first.
  photoHeroAbortActive();
  var token = photoHeroAnimToken;

  try { photoZoomReset(); } catch (_) {}

  let heroMeasureW = 0;
  let heroMeasureH = 0;
  if (photoGalleryMounted && photoGalleryUrls.length > 1) {
    const slideImg = getPhotoZoomImg();
    if (slideImg) {
      heroMeasureW = slideImg.naturalWidth;
      heroMeasureH = slideImg.naturalHeight;
      try {
        imgEl.style.display = '';
        if (slideImg.src) imgEl.src = slideImg.src;
      } catch (_) {}
    }
    unmountPhotoGalleryTrack();
  }

  preparePhotoHeroCloseTarget();

  // Make underlying UI visible immediately on click.
  try { setPhotoSourceBgHidden(false); } catch (_) {}
  try { modalEl.classList.add('photo-modal-closing'); } catch (_) {}
  try { document.body.classList.add('photo-hero-closing'); } catch (_) {}

  var fromRect = photoHeroFromRect;
  // Recompute current contain pixel bounds inside scroller to avoid mismatch.
  const scrollerEl = photoModalScrollerEl;
  var curRect: PixelRect | null = null;
  try {
    const nw = imgEl.naturalWidth || heroMeasureW;
    const nh = imgEl.naturalHeight || heroMeasureH;
    if (scrollerEl && nw && nh) {
      const cr = scrollerEl.getBoundingClientRect();
      const cw = Math.max(1, cr.width);
      const ch = Math.max(1, cr.height);
      var sc = Math.min(cw / nw, ch / nh);
      var tw = nw * sc;
      var th = nh * sc;
      curRect = {
        left: cr.left + (cw - tw) / 2,
        top: cr.top + (ch - th) / 2,
        width: tw,
        height: th
      };
    }
  } catch (_) {}

  if (!curRect || !curRect.width || !curRect.height) {
  photoHeroFromRect = null;
    try { if (photoHeroSourceEl) photoHeroSourceEl.style.visibility = ''; } catch (_) {}
    photoHeroSourceEl = null;
    closePhotoModal();
    return;
  }

  // Hide real image during reverse hero.
  // Не "гасим" — просто скрываем картинку (без opacity), пока летит clone.
  try { modalEl.classList.remove('hero-animating'); } catch (_) {}
  try { imgEl.style.visibility = 'hidden'; } catch (_) {}

  // Create clone at current rect (single global hero clone)
  const clone = document.createElement('img');
  clone.className = 'photo-hero-clone';
  clone.src = imgEl.src;
  clone.alt = '';
  clone.style.left = curRect.left + 'px';
  clone.style.top = curRect.top + 'px';
  clone.style.width = curRect.width + 'px';
  clone.style.height = curRect.height + 'px';
  clone.style.borderRadius = '0px';
  try { if (photoHeroCloneEl) photoHeroCloneEl.remove(); } catch (_) {}
  photoHeroCloneEl = clone;
  document.body.appendChild(clone);

  var dx = fromRect.left - curRect.left;
  var dy = fromRect.top - curRect.top;
  var s = fromRect.width / curRect.width;
  const sourceEl = photoHeroSourceEl;
  const endVisualR =
    photoHeroSourceRadius > 0
      ? photoHeroSourceRadius
      : (sourceEl ? photoHeroReadBorderRadiusPx(sourceEl) : 0);
  var endCloneR = photoHeroRadiusForTransform(endVisualR, s);

  clone.style.transition = 'none';
  clone.style.transformOrigin = 'top left';
  // Start at current position (no transform)
  void clone.offsetHeight;

  requestAnimationFrame(function () {
    try {
      clone.style.transition = photoHeroTransition;
      clone.style.transform = 'translate3d(' + dx.toFixed(2) + 'px,' + dy.toFixed(2) + 'px,0) scale(' + s.toFixed(4) + ')';
      clone.style.borderRadius = (endCloneR > 0 ? endCloneR : 0) + 'px';
    } catch (_) {}
  });

  var done = false;
  var panelsRestoreStarted = false;
  function startPanelsRestoreEarly() {
    if (panelsRestoreStarted) return;
    panelsRestoreStarted = true;
    try { restorePresetDetailPanelsAfterPhoto(); } catch (_) {}
  }
  setTimeout(startPanelsRestoreEarly, 120);
  function finish(): void {
    if (done) return;
    done = true;
    if (token !== photoHeroAnimToken) return;
    try { if (photoHeroCloneEl === clone) photoHeroCloneEl = null; } catch (_) {}
    try { clone.remove(); } catch (_) {}
    startPanelsRestoreEarly();
    photoHeroFromRect = null;
    photoHeroSourceRadius = 0;
    try { if (photoHeroSourceEl) photoHeroSourceEl.style.visibility = ''; } catch (_) {}
    photoHeroSourceEl = null;
    try { imgEl.style.visibility = ''; } catch (_) {}
    modalEl.classList.remove('hero-animating');
    try { document.body.classList.remove('photo-hero-opening'); } catch (_) {}
    try { document.body.classList.remove('photo-hero-closing'); } catch (_) {}
    /* Снимаем open+closing вместе, иначе подложка на кадр снова станет непрозрачной */
    modalEl.classList.remove('photo-modal-open', 'photo-modal-closing');
    try { setPhotoUiLocked(false); } catch (_) {}
    modalEl.setAttribute('aria-hidden', 'true');
    try { imgEl.src = ''; } catch (_) {}
    try { photoZoomReset(); } catch (_) {}
    clearPhotoGallery();
  }

  clone.addEventListener('transitionend', function te(ev: TransitionEvent) {
    if (ev && ev.propertyName && String(ev.propertyName) !== 'transform' && String(ev.propertyName) !== 'border-radius') return;
    finish();
  }, { once: true });
  setTimeout(finish, 420);
}

function closePhotoModal(): void {
  if (!photoModalEl || !photoModalImgEl) return;
  const modalEl = photoModalEl;
  const imgEl = photoModalImgEl;
  clearPhotoOpenDelayTimer();
  if (photoHeroFromRect) {
    // Reverse hero animation back to the thumbnail position.
    return closePhotoModalHero();
  }
  if (photoGalleryUrls.length > 1) {
    try {
      preparePhotoHeroCloseTarget();
    } catch (_) {}
  }
  try { setPhotoSourceBgHidden(false); } catch (_) {}
  try { restorePresetDetailPanelsAfterPhoto(); } catch (_) {}
  try { modalEl.classList.add('photo-modal-closing'); } catch (_) {}
  const backdrop = photoModalBackdropEl;
  let done = false;
  function cleanup(): void {
    if (done) return;
    done = true;
    try { modalEl.classList.remove('photo-modal-open', 'photo-modal-closing'); } catch (_) {}
    try { modalEl.classList.remove('photo-modal-fade-in'); } catch (_) {}
    try { modalEl.style.setProperty('--photo-modal-header-offset', '0px'); } catch (_) {}
    try { document.body.classList.remove('photo-hero-opening'); } catch (_) {}
    try { document.body.classList.remove('photo-hero-closing'); } catch (_) {}
    try { document.body.classList.remove('photo-source-bg-hidden'); } catch (_) {}
    try { document.body.classList.remove('photo-panels-hidden'); } catch (_) {}
    try { document.body.classList.remove('photo-panels-restoring'); } catch (_) {}
    try { document.body.classList.remove('photo-panels-restored'); } catch (_) {}
    try {
      if (photoPanelsRestoreTimer) {
        clearTimeout(photoPanelsRestoreTimer);
        photoPanelsRestoreTimer = null;
      }
    } catch (_) {}
    try { setPhotoUiLocked(false); } catch (_) {}
    modalEl.setAttribute('aria-hidden', 'true');
    try { imgEl.src = ''; } catch (_) {}
    try { photoZoomReset(); } catch (_) {}
    clearPhotoGallery();
  }
  if (backdrop) {
    const onEnd = function (ev: TransitionEvent): void {
      if (ev && ev.target !== backdrop) return;
      if (ev && ev.propertyName && String(ev.propertyName) !== 'opacity') return;
      try { backdrop.removeEventListener('transitionend', onEnd); } catch (_) {}
      cleanup();
    };
    try { backdrop.addEventListener('transitionend', onEnd); } catch (_) {}
    setTimeout(cleanup, 520);
    return;
  }
  cleanup();
}

export function prepareForCartNavigation(): void {
  try {
    const bar = document.getElementById('preset-detail-bottom-bar');
    const br = bar?.getBoundingClientRect();
    recordCartNavigateGesture(br ? br.top + br.height / 2 : window.innerHeight - 40);
  } catch {
    recordCartNavigateGesture(window.innerHeight - 40);
  }
  forceClosePhotoModal();
}

/** Immediate teardown — for view/sheet transitions where hero animation must not linger. */
export function forceClosePhotoModal(): void {
  photoModalSession += 1;
  invalidatePhotoImgLoads();
  clearPhotoOpenDelayTimer();
  photoHeroAbortActive();
  photoHeroFromRect = null;
  photoHeroSourceRadius = 0;
  try {
    preparePhotoHeroCloseTarget();
  } catch (_) {}
  try {
    document
      .querySelectorAll(
        '#view-preset-detail .preset-detail-carousel-slide img, #view-portfolio-detail .preset-detail-carousel-slide img'
      )
      .forEach((node) => {
        if (node instanceof HTMLImageElement) node.style.visibility = '';
      });
  } catch (_) {}
  photoHeroSourceEl = null;
  try {
    if (photoPanelsRestoreTimer) {
      clearTimeout(photoPanelsRestoreTimer);
      photoPanelsRestoreTimer = null;
    }
  } catch (_) {}
  const modalEl = photoModalEl;
  const imgEl = photoModalImgEl;
  if (modalEl) {
    try {
      modalEl.classList.remove(
        'photo-modal-open',
        'photo-modal-closing',
        'photo-modal-fade-in',
        'hero-animating'
      );
    } catch (_) {}
    try {
      modalEl.setAttribute('aria-hidden', 'true');
    } catch (_) {}
    try {
      modalEl.style.setProperty('--photo-modal-header-offset', '0px');
    } catch (_) {}
  }
  if (imgEl) {
    try {
      imgEl.style.opacity = '';
      imgEl.style.visibility = '';
      imgEl.style.transform = '';
      imgEl.src = '';
    } catch (_) {}
  }
  try {
    document.body.classList.remove(
      'photo-hero-opening',
      'photo-hero-closing',
      'photo-source-bg-hidden',
      'photo-panels-hidden',
      'photo-panels-restoring',
      'photo-panels-restored'
    );
  } catch (_) {}
  try {
    photoZoomReset();
  } catch (_) {}
  try {
    setPhotoUiLocked(false);
  } catch (_) {}
  clearPhotoGallery();
}

if (photoModalBackdropEl) photoModalBackdropEl.addEventListener('click', closePhotoModal);
if (photoModalCloseEl) {
  photoModalCloseEl.addEventListener('pointerdown', function (e) {
    if (!e) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    try {
      e.preventDefault();
      e.stopPropagation();
    } catch (_) {}
    closePhotoModal();
  });
}
window.addEventListener('keydown', function (e) {
  if (!e) return;
  if (e.key === 'Escape') closePhotoModal();
  if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) return;
  if (photoGalleryUrls.length <= 1) return;
  if (e.key === 'ArrowLeft') {
    try {
      e.preventDefault();
    } catch (_) {}
    photoGalleryGoTo?.(photoGalleryIndex - 1);
  } else if (e.key === 'ArrowRight') {
    try {
      e.preventDefault();
    } catch (_) {}
    photoGalleryGoTo?.(photoGalleryIndex + 1);
  }
});

if (photoModalScrollerEl && photoModalImgEl) {
  photoModalScrollerEl.addEventListener('wheel', function (e) {
    if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) return;
    if (!isDesktopLikeDevice) return;
    // Пока открыт fullscreen: колесо работает как zoom картинки, а не скролл фона.
    try { if (e) e.preventDefault(); } catch (_) {}
    var baseScale = photoWheelTargetScale;
    var rawDelta = Number(e && e.deltaY ? e.deltaY : 0);
    // Ограничиваем импульс, чтобы на "быстрых" колёсах не было резких скачков.
    var delta = clamp(rawDelta, -180, 180);
    var factor = Math.exp(-delta * 0.0011);
    var nextTarget = clamp(baseScale * factor, 1, 4);
    if (Math.abs(nextTarget - photoWheelTargetScale) < 0.0001 && photoWheelAnimRaf) return;
    photoWheelTargetScale = nextTarget;
    photoWheelAnimStart();
  }, { passive: false });

  photoModalScrollerEl.addEventListener('mousedown', function (e) {
    if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) return;
    if (!isDesktopLikeDevice) return;
    if (!e || e.button !== 0) return;
    if (photoZoomScale <= 1.01) return;
    photoMousePanning = true;
    photoZoomPanning = true;
    photoZoomStartX = e.clientX;
    photoZoomStartY = e.clientY;
    photoZoomStartTx = photoZoomTx;
    photoZoomStartTy = photoZoomTy;
    photoWheelAnimStop();
    try { e.preventDefault(); } catch (_) {}
    try { photoZoomUpdatePanCursor(); } catch (_) {}
  }, { passive: false });

  window.addEventListener('mousemove', function (e) {
    if (!photoMousePanning) return;
    if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) {
      photoMousePanning = false;
      photoZoomPanning = false;
      try { photoZoomUpdatePanCursor(); } catch (_) {}
      return;
    }
    photoZoomTx = photoZoomStartTx + (e.clientX - photoZoomStartX);
    photoZoomTy = photoZoomStartTy + (e.clientY - photoZoomStartY);
    photoZoomApply();
    try { e.preventDefault(); } catch (_) {}
  }, { passive: false });

  window.addEventListener('mouseup', function () {
    if (!photoMousePanning) return;
    photoMousePanning = false;
    photoZoomPanning = false;
    try { photoZoomUpdatePanCursor(); } catch (_) {}
  });

  photoModalScrollerEl.addEventListener('touchstart', function (e) {
    if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) return;
    if (!e || !e.touches) return;
    if (e.touches.length === 2) {
      try { photoGalleryDragCancel?.(); } catch (_) {}
      photoZoomPinching = true;
      photoZoomPanning = false;
      photoWheelAnimStop();
      var dx = e.touches[0].clientX - e.touches[1].clientX;
      var dy = e.touches[0].clientY - e.touches[1].clientY;
      photoZoomStartDist = Math.sqrt(dx * dx + dy * dy);
      photoZoomStartScale = photoZoomScale;
      try { e.preventDefault(); } catch (_) {}
      return;
    }
    if (e.touches.length === 1 && photoZoomScale > 1.01) {
      photoZoomPanning = true;
      photoZoomPinching = false;
      photoZoomStartX = e.touches[0].clientX;
      photoZoomStartY = e.touches[0].clientY;
      photoZoomStartTx = photoZoomTx;
      photoZoomStartTy = photoZoomTy;
    }
  }, { passive: false });

  photoModalScrollerEl.addEventListener('touchmove', function (e) {
    if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) return;
    if (!e || !e.touches) return;
    if (photoZoomPinching && e.touches.length === 2) {
      try { e.preventDefault(); } catch (_) {}
      var dx = e.touches[0].clientX - e.touches[1].clientX;
      var dy = e.touches[0].clientY - e.touches[1].clientY;
      var dist = Math.sqrt(dx * dx + dy * dy);
      if (!photoZoomStartDist) return;
      var nextScale = clamp(photoZoomStartScale * (dist / photoZoomStartDist), 1, 4);
      photoZoomScale = nextScale;
      photoZoomApply();
      return;
    }
    if (photoZoomPanning && e.touches.length === 1 && photoZoomScale > 1) {
      try { e.preventDefault(); } catch (_) {}
      var x = e.touches[0].clientX;
      var y = e.touches[0].clientY;
      photoZoomTx = photoZoomStartTx + (x - photoZoomStartX);
      photoZoomTy = photoZoomStartTy + (y - photoZoomStartY);
      photoZoomApply();
    }
  }, { passive: false });

  function photoZoomTouchEnd(e: TouchEvent) {
    if (!photoModalEl || !photoModalEl.classList.contains('photo-modal-open')) return;
    if (!e) return;
    var remaining = e.touches ? e.touches.length : 0;

    if (remaining >= 2) return;

    if (remaining === 1 && photoZoomPinching) {
      photoZoomPinching = false;
      if (photoZoomScale > 1.01) {
        photoZoomPanning = true;
        photoZoomStartX = e.touches[0].clientX;
        photoZoomStartY = e.touches[0].clientY;
        photoZoomStartTx = photoZoomTx;
        photoZoomStartTy = photoZoomTy;
      }
      return;
    }

    photoZoomPinching = false;
    photoZoomPanning = false;
    photoZoomStartDist = 0;

    if (photoZoomScale <= 1.01) {
      photoZoomScale = 1;
      photoWheelTargetScale = 1;
      photoZoomTx = 0;
      photoZoomTy = 0;
      photoZoomApply();
    }
  }

  photoModalScrollerEl.addEventListener('touchend', photoZoomTouchEnd);
  photoModalScrollerEl.addEventListener('touchcancel', photoZoomTouchEnd);
}

initPhotoModalGalleryCarousel();

